import RadioIcon from '@mui/icons-material/Radio';
import TvIcon from '@mui/icons-material/Tv';
import { isVideoStation } from '@app/core/stations/helpers';
import { useStations } from '@app/core/stations/hooks/useStations';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useStatusQueryFilter } from '../../hooks/useStatusQueryFilter';
import { StationTypeFilterType } from '../options';

/**
 * Non-exclusive toggle group: TV and/or Radio. No type param = show all (both selected in UI).
 * When both or none selected, the `type` param is removed from the URL.
 * Renders nothing when the user has only one station type (all TV or all Radio).
 */
export function StatusStationTypeFilter() {
  const { $t } = useIntl();
  const stations = useStations();
  const { queryParams, setQueryParams } = useStatusQueryFilter();

  const hasMultipleStationTypes = useMemo(() => {
    const types = new Set(
      stations.map((s) =>
        isVideoStation(s) ? StationTypeFilterType.TV : StationTypeFilterType.RADIO,
      ),
    );
    return types.size > 1;
  }, [stations]);

  const selectedTypes = useMemo(() => {
    if (queryParams.type == null) {
      return [StationTypeFilterType.TV, StationTypeFilterType.RADIO];
    }

    return [queryParams.type];
  }, [queryParams.type]);

  if (!hasMultipleStationTypes) {
    return null;
  }

  const handleChange = (
    _event: React.MouseEvent<HTMLElement>,
    newValue: StationTypeFilterType[],
  ) => {
    if (newValue.length === 0 || newValue.length === 2) {
      setQueryParams({ type: null });
    } else {
      setQueryParams({ type: newValue[0] });
    }
  };

  return (
    <ToggleButtonGroup
      value={selectedTypes}
      onChange={handleChange}
      aria-label={$t({
        id: 'logger.statusScreen.stationType',
        defaultMessage: 'Station type',
      })}
      size="small"
    >
      <ToggleButton
        value={StationTypeFilterType.TV}
        aria-label={$t({
          id: 'logger.statusScreen.stationTypeTv',
          defaultMessage: 'TV',
        })}
        color="info"
      >
        <TvIcon fontSize="small" />
      </ToggleButton>
      <ToggleButton
        value={StationTypeFilterType.RADIO}
        aria-label={$t({
          id: 'logger.statusScreen.stationTypeRadio',
          defaultMessage: 'Radio',
        })}
        color="info"
      >
        <RadioIcon fontSize="small" />
      </ToggleButton>
    </ToggleButtonGroup>
  );
}
